/**
 * Critérios da Apresentação Técnica — Regulamento FRL 2026, item 4.2.5
 * (total de 100 pontos) — e a dinâmica do item 4.2.3: 10 minutos de
 * apresentação + 5 de perguntas da banca.
 */
export type TCriterionKey =
  | 'mechanical_score'
  | 'programming_score'
  | 'teamwork_score'
  | 'communication_score';

export interface ICriterion {
  key: TCriterionKey;
  label: string;
  max: number;
  /** Perguntas-guia do regulamento, para apoiar a banca. */
  questions: string[];
}

export const CRITERIA: ICriterion[] = [
  {
    key: 'mechanical_score',
    label: 'Montagem mecânica e engenharia',
    max: 30,
    questions: [
      'O robô tem um design robusto e eficiente para as missões?',
      'A equipe mostrou criatividade e bom uso dos blocos de montar e das conexões físicas?',
    ],
  },
  {
    key: 'programming_score',
    label: 'Lógica de programação e integração',
    max: 30,
    questions: [
      'A equipe explica com clareza o código feito no Micro:bit?',
      'Entende como os sensores (câmera com IA, infravermelho) se comunicam com motores e atuadores?',
    ],
  },
  {
    key: 'teamwork_score',
    label: 'Trabalho em equipe e organização',
    max: 20,
    questions: [
      'A divisão de tarefas foi equilibrada e justa?',
      'Todos participaram da apresentação e mostraram entrosamento?',
    ],
  },
  {
    key: 'communication_score',
    label: 'Clareza e comunicação',
    max: 20,
    questions: [
      'Apresentou as ideias com clareza, respeitando os 10 minutos?',
      'Respondeu às perguntas da banca com segurança e autonomia?',
    ],
  },
];

export const MAX_TOTAL = CRITERIA.reduce((sum, c) => sum + c.max, 0);

export type TPresentationScores = Record<TCriterionKey, number>;

export const EMPTY_SCORES: TPresentationScores = {
  mechanical_score: 0,
  programming_score: 0,
  teamwork_score: 0,
  communication_score: 0,
};

export function clampScore(key: TCriterionKey, value: number): number {
  const criterion = CRITERIA.find(c => c.key === key);
  const max = criterion?.max ?? 0;
  if (!Number.isFinite(value)) return 0;
  return Math.min(max, Math.max(0, Math.round(value)));
}

export function totalScore(scores: TPresentationScores): number {
  return CRITERIA.reduce((sum, c) => sum + clampScore(c.key, scores[c.key]), 0);
}

export const PRESENTATION_SECONDS = 10 * 60;
export const QUESTIONS_SECONDS = 5 * 60;

export interface ITimerPhase {
  phase: 'presentation' | 'questions' | 'over';
  /** Segundos restantes na fase atual (0 quando acabou). */
  remaining: number;
}

/** Em qual parte dos 15 minutos a banca está. */
export function timerPhase(elapsedSeconds: number): ITimerPhase {
  if (elapsedSeconds < PRESENTATION_SECONDS) {
    return {
      phase: 'presentation',
      remaining: PRESENTATION_SECONDS - elapsedSeconds,
    };
  }
  const intoQuestions = elapsedSeconds - PRESENTATION_SECONDS;
  if (intoQuestions < QUESTIONS_SECONDS) {
    return { phase: 'questions', remaining: QUESTIONS_SECONDS - intoQuestions };
  }
  return { phase: 'over', remaining: 0 };
}
